<template>
  <v-container fluid class="qmkey-menus-view">
    <v-row dense class="d-flex mb-0 pb-0" style="height: 50px">
      <!-- View Title -->
      <v-col class="d-flex mt-1 mb-0 pb-0 ml-2" md="auto">
        <ViewTitle title="Users" />
      </v-col>
      <v-col class="d-flex mb-0 pb-0" >
        <v-btn
          rounded
          small
          class="add-button"
          @click.stop="addUserForm()"
          >
            Add User
        </v-btn>
      </v-col>
    </v-row>
    <v-data-table
      fixed
      small
      :items="users"
      :headers="fields"
      dense
      item-class="qmkey-item-row"
      sort-by="last_name"
    >
      <template v-slot:item.username="{ item }">
        <v-btn
          @click.stop="editUserForm(item.id)"
          text
          small
          class="qmkey-add-button-menu"
        >
          {{ item.username }}
        </v-btn>
      </template>
      <template v-slot:item.isAdmin="{item}">
        <b v-if="item.isAdmin" class="text-success">✔</b>
      </template>
      <template v-slot:item.isManager="{item}">
        <b v-if="item.isManager" class="text-success">✔</b>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon
          small
          class="mr-2"
          @click.stop="editUserForm(item.id)"
        >
          mdi-pencil
        </v-icon>
        <v-icon
          small
          icon
          class="mr-2"
          @click.stop="deleteUserForm(item.id)"
        >
          mdi-trash-can-outline
        </v-icon>
      </template>
    </v-data-table>
    <!-- Add / Edit User Modal -->
    <v-dialog
      persistent
      max-width="600px"
      v-model="showEdit"
    >
      <v-card
        class="mx-auto"
        :loading="loading">
        <v-card-title>
          <span class="headline">{{ modalTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn
            text @click="close"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle
          class="pb-0"
          v-if="modalSubTitle"
        >
          {{ modalSubTitle }}
        </v-card-subtitle>
        <v-card-text>
          <v-form
            ref="editUser"
            v-model="valid"
            autocomplete="off"
            dense
          >
            <v-alert
              :value="alert"
              color="error"
              dark
              icon="mdi-alert"
              transition="scale-transition"
              outlined
              dismissible
              @input="alert=false"
            >
              {{ message }}
            </v-alert>
            <v-text-field
              id="username"
              v-model="currentEditUser.username"
              type="text"
              required
              placeholder="Enter username"
              label="Username"
              :rules="[rules.required]"
            >
            </v-text-field>
            <v-text-field
              id="email"
              v-model="currentEditUser.email"
              type="text"
              required
              placeholder="Enter email"
              label="Email"
              :rules="[rules.required, rules.email]"
              autocomplete="off"
            >
            </v-text-field>
            <v-text-field
              id="password"
              v-model="currentEditUser.password"
              type="password"
              required
              placeholder="Enter password"
              label="Password"
              :rules="new_user && [rules.required] || []"
            >
            </v-text-field>
            <v-text-field
              id="first_name"
              v-model="currentEditUser.first_name"
              type="text"
              required
              placeholder="Enter name"
              label="Name"
              :rules="[rules.required]"
            >
            </v-text-field>
            <v-text-field
              id="last_name"
              v-model="currentEditUser.last_name"
              type="text"
              required
              placeholder="Enter last name"
              label="Last name"
              :rules="[rules.required]"
            >
            </v-text-field>
            <v-select
              id="roles"
              v-model="currentEditUser.roles"
              multiple
              :items="allRoles"
              label="Roles"
              placeholder="Choose User Roles"
            >
            </v-select>
            <v-checkbox
              v-model="currentEditUser.isManager"
              label="Is Manager"
            >
            </v-checkbox>
            <v-checkbox
              v-model="currentEditUser.isAdmin"
              label="Is Admin"
            >
            </v-checkbox>
            <v-file-input
              id="user_photo"
              label="Photo"
              placeholder="Pick photo"
              prepend-icon="mdi-camera"
              @change="previewImage"
              v-model="photo"
            ></v-file-input>
          </v-form>
          <v-img
            max-height="200px"
            max-width="200px"
            :src="getPhoto"
          >
          </v-img>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="mr-4"
            @click="processForm"
            :disabled="!valid"
          >
            Ok
          </v-btn>
          <v-btn @click="clear">Clear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Delete user modal -->
    <v-dialog max-width="500px" v-model="showDelete">
      <v-card
        class="mx-auto"
        :loading="loading"
        >
        <v-card-title>
          <span class="headline">{{ modalTitle }}</span>
        </v-card-title>
        <v-card-text>
          Do you want to delete user 
          <strong>{{currentEditUser.username}}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="mr-4" @click="processDelete">Ok</v-btn>
          <v-btn @click="showDelete=false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog> 
  </v-container>

</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'

import ViewTitle from '@/components/ViewTitle'
// TODO. Move that to API methods
import User from '@/api/User'
import Roles from '@/api/Roles'

export default {
  name: 'UsersView',
  components: {
    ViewTitle,
  },

  data: function() {
    return {
      alert: false,
      showEdit: false,
      loading: false,
      showDelete: false,
      message: null,
      currentEditUser: { roles: [] }, // <b-form-select> needs a list
      modalTitle: '',
      modalSubTitle: '',
      allRoles: [],
      fields: [
        { value: 'username', text: 'Username', sortable: true},
        { value: 'first_name', text: 'Name', sortable: true },
        { value: 'last_name', text: 'Last Name', sortable: true },
        { value: 'email', text: 'Email' },
        { value: 'isAdmin', text: 'Admin' },
        { value: 'isManager', text: 'Manager'},
        { value: 'site_name', text: 'Site', sortable: true},
        { value: 'actions', text:'', sortable:false },
      ],
      rules: {
        required: value => !!value || "Required.",
        email: value => /.+@.+/.test(value) || 'E-mail must be valid' 
      },
      valid: false,
      // disable: false,
      new_user: true,
      photo_url:'',
      photo: null,
    }
  },

  computed: {
    ...mapState(['sitePath', 'isLoading', 'users']),
    getPhoto(){
      if (this.photo_url) {
        return this.photo_url
      }
      else if (this.currentEditUser.photo){
        console.log('Warning harcoded url Users.getPhoto', this.currentEditUser.photo)
        return 'http://localhost:8000' + this.currentEditUser.photo
      }
    },
  },

  mounted: function() {
    this.SET_CURRENT_SITE(this.$session.get('site'))
    this.GET_USERS(this.sitePath)
    this.getRoles()
  },

  methods: {
    ...mapActions(['GET_USERS']),
    ...mapMutations(['SET_CURRENT_SITE']),

    addUserForm() {
      this.currentEditUser = { roles: [] }
      this.modalTitle = 'Add new user'
      this.message = null
      this.showEdit = true
      this.new_user = true
      this.alert = false
    },

    editUserForm(id) {
      User.get(this.sitePath, id)
        .then(response => {
          this.currentEditUser = response
          this.modalTitle = `Edit user #${id}`
          this.message = null
          this.showEdit = true
          this.new_user = false
          this.photo_url = response.photo
        })
        .catch(err => {
          this.render_form_errors(err)
          this.alert = true
        })
    },

    deleteUserForm(id) {
      User.get(this.sitePath, id)
        .then(response => {
          this.currentEditUser = response
          this.modalTitle = `Delete user #${id} ${this.currentEditUser.first_name} ${this.currentEditUser.last_name}`
          this.showDelete = true
        })
        .catch(err => {
          alert(err)
        })
    },

    addUser() {
      this.currentEditUser.photo = this.photo
      let edit_form = this.$refs.editUser.$el
      var form_data = new FormData()
      for ( var key in this.currentEditUser ) {
        if (this.currentEditUser[key] !=null){
          form_data.append(key, this.currentEditUser[key])
        }
      }
      User.postUser(this.sitePath, form_data)      
        .then(response => {
          this.GET_USERS(this.sitePath)
          this.showEdit = false
          this.clear()
        })
        .catch(err => {
          this.render_form_errors(err)
          this.alert = true
        })
    },

    deleteUser() {
      User.delete(this.sitePath, this.currentEditUser.id)
        .then(response => {
          this.GET_USERS(this.sitePath)
          this.showDelete = false
        })
        .catch(err => {
          this.render_form_errors(err)
        })
    },

    updateUser() {
      this.currentEditUser.photo = this.photo
      let edit_form = this.$refs.editUser.$el
      var form_data = new FormData()
      for ( var key in this.currentEditUser ) {
        if (this.currentEditUser[key] !=null){
          form_data.append(key, this.currentEditUser[key])
        }
      }
      User.putUser(this.sitePath, this.currentEditUser.id, form_data)
        .then(response => {
          this.GET_USERS(this.sitePath)
          this.showEdit = false
          this.clear()
        })
        .catch(err => {
          this.render_form_errors(err)
          this.alert = true
        })
    },
    processDelete(evt) {
      if (evt) evt.preventDefault()
      this.deleteUser()
    },
    processForm(evt) {
      if (evt) evt.preventDefault()
      // check if id already exists
      if (this.currentEditUser.id !== undefined) {
        this.updateUser()
      } else {
        this.addUser()
      }
    },
    getRoles() {
      Roles.get(this.sitePath)
        .then(response => {
          this.allRoles = []
          for (var role of response.results) {
            this.allRoles.push({ value: role.id, text: role.name })
          }
        })
        .catch(err => {
          console.log(err)
        })
    },
    clear: function() {
      this.message = '';
      this.alert = false;
      this.$refs.editUser.resetValidation()
      this.currentEditUser = { roles: [] }
      this.photo_url = ''
      this.photo = null
    },
    close: function() {
      this.$refs.editUser.reset()
      this.message = '';
      this.alert = false;
      this.showEdit = false
      this.currentEditUser = {}
      this.photo_url = ''
      this.photo = null
    },
    previewImage(evt){
      if (evt) {
        this.photo_url = URL.createObjectURL(evt)
      }
      else{
        this.photo_url = ''
      }
    },    
  },
}
</script>


<style lang="scss">
  @import "@/sass/menus.scss";
</style>
